

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchProductById, checkout } from '../services/api';
import './ProductDetail.css';
import axios from "axios";

import { API_URL } from '../services/api';

function Checkout() {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);
  const [productImages, setProductImages] = useState({});
  const [address, setAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [formError, setFormError] = useState(null);

  const [formData, setFormData] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    first_name: '',
    last_name: '',
    phone_number: ''
  });

  const token = localStorage.getItem('access_token');

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API_URL}api/cart/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCart(response.data);

      const images = {};
      for (const item of response.data.items) {
        const productId = item.product;
        const productData = await fetchProductById(productId);

        // Get the first image from the product's images array
        const productImage = productData.images && productData.images.length > 0
          ? productData.images[0].image
          : null;

        // If this is a variant item, check for variant-specific images
        if (item.variant_id) {
          const variantData = productData.variants.find(v => v.id === item.variant_id);
          const variantImage = variantData?.images && variantData.images.length > 0
            ? variantData.images[0].image
            : productImage; // Fall back to product image if variant has no images

          images[`variant_${item.variant_id}`] = variantImage;
        }

        // Store the product image
        images[`product_${productId}`] = productImage;
      }

      setProductImages(images);
    } catch (err) {
      setError('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const fetchAddress = async () => {
    try {
      const res = await axios.get(`${API_URL}api/set-shipping-address/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAddress(res.data);
    } catch {
      setAddress(null);
    }
  };

  useEffect(() => {
    fetchCart();
    fetchAddress();
  }, [token]);

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    const cleanedData = {
      ...formData,
      address_line1: formData.address_line1.trim(),
      address_line2: formData.address_line2.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      postal_code: formData.postal_code.trim(),
      country: formData.country.trim(),
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      phone_number: formData.phone_number.trim()
    };

    // Basic required field validation
    const requiredFields = ['address_line1', 'city', 'state', 'postal_code', 'country', 'first_name', 'last_name', 'phone_number'];
    const missingFields = requiredFields.filter(field => !cleanedData[field]);

    if (missingFields.length > 0) {
      setFormError('Please fill in all required fields.');
      return;
    }

    try {
      await axios.post(`${API_URL}api/set-shipping-address/`, cleanedData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowAddressForm(false);
      setAddress(cleanedData);

      // Re-fetch the cart after the address is updated to reflect the new shipping fees
      fetchCart(); // This will update the cart with the new shipping fees

    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid address data. Please check your inputs.';
      console.error('Address submission error:', err.response?.data || err.message);
      setFormError(message);
    }
  };

  const handleCheckout = async () => {
    setProcessingOrder(true);
    try {
      const orderData = await checkout(token, cart);
      setOrder(orderData);
      if (orderData.checkout_url) {
        window.location.href = orderData.checkout_url;
      }
    } catch {
      setError('Failed to complete checkout');
      setProcessingOrder(false);
    }
  };

  // Loading spinner overlay
  const LoadingSpinner = () => (
    <div className="loading-overlay">
      <div className="spinner-container">
        <div className="spinner"></div>
        <p>Loading your cart...</p>
      </div>
    </div>
  );

  // Processing order spinner overlay
  const ProcessingOrderSpinner = () => (
    <div className="loading-overlay">
      <div className="spinner-container">
        <div className="spinner"></div>
        <p>Processing your order. You'll be redirected to Stripe to complete your payment...</p>
      </div>
    </div>
  );

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="error-message">{error}</div>;
  if (!cart || cart.items.length === 0) return <div className="empty-cart-message">Your cart is empty</div>;

  const productTotal = cart.items.reduce((acc, item) => acc + parseFloat(item.total_price), 0);
  const subtotal = parseFloat(cart.total_shipping_fees) + productTotal;

  return (
    <div className="checkout-container">
      {processingOrder && <ProcessingOrderSpinner />}

      <div className="checkout-header">
        <h1>Checkout</h1>
      </div>

      <div className="checkout-content">
        <div className="checkout-items-section">
          <h2>Order Summary</h2>
          <ul className="cart-items-list">
            {cart.items.map(item => {
              const productId = item.product;
              const imageUrl = item.variant_id
                ? productImages[`variant_${item.variant_id}`] || productImages[`product_${productId}`]
                : productImages[`product_${productId}`];

              return (
                <li key={item.id} className="cart-item">
                  <div className="cart-item-content">
                    <div className="cart-item-image">
                      <img src={imageUrl || ''} alt={item.product_name} />
                    </div>
                    <div className="cart-item-details">
                      <h3>{item.product_name}</h3>
                      {item.variant_option1 && <p className="variant-option">Selection: {item.variant_option1}</p>}
                      {item.variant_option2 && <p className="variant-option">Option 2: {item.variant_option2}</p>}
                      <div className="cart-item-pricing">
                        <p className="item-price">Price: ${item.product_price}</p>
                        <p className="item-quantity">Quantity: {item.quantity}</p>
                        <p className="item-total">Total: ${item.total_price}</p>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="checkout-shipping-section">
          <h2>Shipping Address</h2>
          {address ? (
            <div className="saved-address">
              <div className="address-card">
                <p className="address-name">{address.first_name} {address.last_name}</p>
                <p>{address.address_line1}</p>
                {address.address_line2 && <p>{address.address_line2}</p>}
                <p>{address.city}, {address.state} {address.postal_code}</p>
                <p>{address.country}</p>
                <p>Phone: {address.phone_number}</p>
              </div>
              <button className="edit-address-btn" onClick={() => setShowAddressForm(true)}>Edit Address</button>
            </div>
          ) : (
            <div className="no-address">
              <p>Please add a shipping address to continue</p>
              <button className="add-address-btn" onClick={() => setShowAddressForm(true)}>Add Address</button>
            </div>
          )}

          {showAddressForm && (
            <div className="address-form-container">
              <h3>Enter Shipping Address</h3>
              <form onSubmit={handleAddressSubmit} className="address-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="first_name">First Name *</label>
                    <input
                      id="first_name"
                      type="text"
                      value={formData.first_name}
                      onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="last_name">Last Name *</label>
                    <input
                      id="last_name"
                      type="text"
                      value={formData.last_name}
                      onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address_line1">Address Line 1 *</label>
                  <input
                    id="address_line1"
                    type="text"
                    value={formData.address_line1}
                    onChange={e => setFormData({ ...formData, address_line1: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address_line2">Address Line 2 (optional)</label>
                  <input
                    id="address_line2"
                    type="text"
                    value={formData.address_line2}
                    onChange={e => setFormData({ ...formData, address_line2: e.target.value })}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      id="city"
                      type="text"
                      value={formData.city}
                      onChange={e => setFormData({ ...formData, city: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="state">State/Province *</label>
                    <input
                      id="state"
                      type="text"
                      value={formData.state}
                      onChange={e => setFormData({ ...formData, state: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="postal_code">Postal Code *</label>
                    <input
                      id="postal_code"
                      type="text"
                      value={formData.postal_code}
                      onChange={e => setFormData({ ...formData, postal_code: e.target.value })}
                      required
                    />
                  </div>
                  {/* <div className="form-group">
                    <label htmlFor="country">Country Code (e.g., US) *</label>
                    <input 
                      id="country"
                      type="text" 
                      value={formData.country} 
                      onChange={e => setFormData({ ...formData, country: e.target.value })} 
                      required
                    />
                  </div> */}

                  <div className="form-group">
                    <label htmlFor="country">Country  *</label>
                    <select
                      id="country"
                      value={formData.country}
                      onChange={e => setFormData({ ...formData, country: e.target.value })}
                      required
                    >
                      <option value="US">United States (US)</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="phone_number">Phone Number *</label>
                  <input
                    id="phone_number"
                    type="tel"
                    value={formData.phone_number}
                    onChange={e => setFormData({ ...formData, phone_number: e.target.value })}
                    required
                  />
                </div>

                {formError && <p className="form-error">{formError}</p>}

                <div className="form-buttons">
                  <button type="submit" className="save-address-btn">Save Address</button>
                  <button type="button" className="cancel-btn" onClick={() => setShowAddressForm(false)}>Cancel</button>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className="checkout-summary">
          <h2>Order Total</h2>
          <div className="summary-row">
            <span>Items Subtotal:</span>
            <span>${productTotal.toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>Shipping Fee:</span>
            <span>${parseFloat(cart.total_shipping_fees).toFixed(2)}</span>
          </div>
          <div className="summary-row total">
            <span>Total:</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>

          {/* Free Shipping Progress Bar */}
          <div className="free-shipping-progress">
            <div className="progress-header">
              <span className="progress-text">
                {productTotal >= 75
                  ? "🎉 You qualify for FREE shipping!"
                  : `Add $${(75 - productTotal).toFixed(2)} more for FREE shipping`
                }
              </span>
              <span className="progress-amount">${productTotal.toFixed(2)} / $75.00</span>
            </div>
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${Math.min((productTotal / 75) * 100, 100)}%` }}
              ></div>
            </div>
          </div>

          <div className="payment-note">
            <p>You'll be redirected to Stripe's secure payment page to enter your billing information and complete your purchase.</p>
          </div>

          <button
            onClick={handleCheckout}
            className="checkout-button"
            disabled={!address || processingOrder}
          >
            {processingOrder ? 'Processing...' : 'Complete Checkout'}
          </button>
        </div>
      </div>

      <style jsx>{`
        .checkout-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
          position: relative;
        }
        
        .checkout-header h1 {
          font-size: 2rem;
          margin-bottom: 2rem;
          font-weight: 600;
          color: #333;
          border-bottom: 1px solid #eee;
          padding-bottom: 1rem;
        }
        
        .checkout-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }
        
        @media (max-width: 768px) {
          .checkout-content {
            grid-template-columns: 1fr;
          }
        }
        
        .checkout-items-section, 
        .checkout-shipping-section,
        .checkout-summary {
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
          padding: 1.5rem;
          margin-bottom: 2rem;
        }
        
        .checkout-items-section h2,
        .checkout-shipping-section h2,
        .checkout-summary h2 {
          margin-top: 0;
          margin-bottom: 1.5rem;
          font-size: 1.25rem;
          color: #333;
          font-weight: 600;
        }
        
        .cart-items-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .cart-item {
          padding: 1rem 0;
          border-bottom: 1px solid #eee;
        }
        
        .cart-item:last-child {
          border-bottom: none;
        }
        
        .cart-item-content {
          display: flex;
          align-items: center;
        }
        
        .cart-item-image {
          width: 80px;
          height: 80px;
          margin-right: 1rem;
          flex-shrink: 0;
        }
        
        .cart-item-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 4px;
        }
        
        .cart-item-details {
          flex-grow: 1;
        }
        
        .cart-item-details h3 {
          margin: 0 0 0.5rem;
          font-size: 1rem;
          font-weight: 600;
          color: #333;
        }
        
        .variant-option {
          font-size: 0.875rem;
          color: #666;
          margin: 0.25rem 0;
        }
        
        .cart-item-pricing {
          margin-top: 0.5rem;
          font-size: 0.875rem;
          color: #555;
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }
        
        .cart-item-pricing p {
          margin: 0;
        }
        
        .item-total {
          font-weight: 600;
          color: #333;
        }
        
        .saved-address {
          margin-bottom: 1.5rem;
        }
        
        .address-card {
          background: #f9f9f9;
          border-radius: 6px;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        
        .address-name {
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        
        .address-card p {
          margin: 0.25rem 0;
          font-size: 0.875rem;
          color: #555;
        }
        
        .edit-address-btn, 
        .add-address-btn {
          background: #f5f5f5;
          border: 1px solid #ddd;
          color: #333;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }
        
        .edit-address-btn:hover, 
        .add-address-btn:hover {
          background: #e9e9e9;
        }
        
        .no-address {
          text-align: center;
          padding: 2rem 0;
        }
        
        .no-address p {
          margin-bottom: 1rem;
          color: #666;
        }
        
        .address-form-container {
          background: #f9f9f9;
          border-radius: 6px;
          padding: 1.5rem;
          margin-top: 1.5rem;
        }
        
        .address-form-container h3 {
          margin-top: 0;
          margin-bottom: 1.5rem;
          font-size: 1rem;
          color: #333;
        }
        
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        
        .form-group {
          margin-bottom: 1rem;
        }
        
        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
          color: #555;
        }
        
        .form-group input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 0.875rem;
        }
        
        .form-error {
          color: #d32f2f;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }
        
        .form-buttons {
          display: flex;
          gap: 1rem;
          margin-top: 1rem;
        }
        
        .save-address-btn {
          background: #4CAF50;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s ease;
        }
        
        .save-address-btn:hover {
          background: #43A047;
        }
        
        .cancel-btn {
          background: transparent;
          color: #555;
          border: 1px solid #ddd;
          padding: 0.75rem 1.5rem;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        }
        
        .cancel-btn:hover {
          background: #f5f5f5;
        }
        
        .checkout-summary {
          grid-column: 2;
          grid-row: 1 / span 2;
        }
        
        @media (max-width: 768px) {
          .checkout-summary {
            grid-column: 1;
            grid-row: auto;
          }
        }
        
        .summary-row {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 0;
          font-size: 0.875rem;
          color: #555;
          border-bottom: 1px solid #eee;
        }
        
        .summary-row.total {
          font-weight: 600;
          font-size: 1.125rem;
          color: #333;
          padding: 1rem 0;
          margin-top: 0.5rem;
        }
        
        .payment-note {
          margin: 1.5rem 0;
          padding: 1rem;
          background: #f9f9f9;
          border-radius: 6px;
          border-left: 4px solid #4CAF50;
        }
        
        .payment-note p {
          margin: 0;
          font-size: 0.875rem;
          color: #555;
        }
        
        .checkout-button {
          width: 100%;
          background: #4CAF50;
          color: white;
          border: none;
          padding: 1rem;
          border-radius: 4px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s ease;
        }
        
        .checkout-button:hover {
          background: #43A047;
        }
        
        .checkout-button:disabled {
          background: #9E9E9E;
          cursor: not-allowed;
        }
        
        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.8);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        
        .spinner-container {
          text-align: center;
        }
        
        .spinner {
          display: inline-block;
          width: 50px;
          height: 50px;
          border: 4px solid rgba(76, 175, 80, 0.3);
          border-radius: 50%;
          border-top-color: #4CAF50;
          animation: spin 1s ease-in-out infinite;
          margin-bottom: 1rem;
        }
        
        .spinner-container p {
          color: #4CAF50;
          font-weight: 500;
          max-width: 300px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .error-message {
          text-align: center;
          padding: 2rem;
          background: #ffebee;
          color: #d32f2f;
          border-radius: 8px;
          margin: 2rem auto;
          max-width: 600px;
        }
        
        .empty-cart-message {
          text-align: center;
          padding: 4rem 2rem;
          color: #666;
          font-size: 1.25rem;
        }


        .free-shipping-progress {
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%);
  border: 1px solid #e3f2fd;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.1);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.progress-text {
  font-weight: 600;
  color: #1976d2;
  font-size: 0.95rem;
}

.progress-amount {
  font-weight: 500;
  color: #555;
  font-size: 0.875rem;
}

.progress-bar-container {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50 0%, #66bb6a 50%, #81c784 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
  position: relative;
}

.progress-bar-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@media (max-width: 768px) {
  .progress-header {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  
  .progress-text {
    font-size: 0.875rem;
  }
}
      `}</style>
    </div>
  );
}

export default Checkout;